/**
 * Freight Outstanding · Sprint 10 · 2026-08-19.
 *
 * Operational visibility for fuel purchases that are complete and
 * already in inventory, but whose freight invoice has not yet
 * arrived. Powered by ``GET /api/receiving/freight/outstanding`` —
 * additive read-only report. NEVER interpreted as an incomplete
 * purchase; PROVISIONAL means the landed cost is pending a separate
 * freight transaction.
 */
import { useEffect, useState } from "react";
import { RefreshCw, Truck } from "lucide-react";

import { api } from "@/lib/api";

const REFRESH_MS = 60_000;

const AGING = [
  { key: "0-3",  label: "0–3 days",  tone: "bg-emerald-50 text-emerald-900 border-emerald-300" },
  { key: "4-7",  label: "4–7 days",  tone: "bg-amber-50 text-amber-900 border-amber-300" },
  { key: "8-14", label: "8–14 days", tone: "bg-orange-50 text-orange-900 border-orange-300" },
  { key: "15+",  label: "15+ days",  tone: "bg-rose-50 text-rose-900 border-rose-300" },
];

const fmtL = (v) => {
  if (v == null || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(n) ? `${Math.round(n).toLocaleString()} L` : "—";
};
const fmtMoney = (v) => {
  if (v == null || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(n) ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";
};

export default function FreightOutstanding() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/receiving/freight/outstanding");
      setData(res.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load freight outstanding.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (error)   return <div className="p-6 text-sm text-rose-700">{error}</div>;
  if (!data)   return null;

  const buckets = data.aging_buckets || {};
  return (
    <div className="p-6 space-y-5" data-testid="freight-outstanding-page">
      <header className="flex items-baseline justify-between border-b border-slate-200 pb-3">
        <div>
          <h1 className="font-display text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Truck size={20} /> Freight Outstanding
          </h1>
          <p className="mt-1 text-xs italic text-slate-500 max-w-3xl">
            {data.policy_note ||
              "Fuel purchase is complete and in inventory; final landed cost is pending a separate freight transaction."}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
          data-testid="freight-outstanding-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </header>

      {/* Summary strip */}
      <section className="grid grid-cols-6 gap-3">
        <SummaryCard label="Fuel purchases awaiting freight" value={data.count ?? 0}
                     testid="freight-outstanding-count" />
        <SummaryCard label="Total litres" value={fmtL(data.total_litres)}
                     testid="freight-outstanding-litres" />
        {AGING.map((a) => (
          <div key={a.key}
               data-testid={`freight-aging-${a.key}`}
               className={`rounded border px-3 py-2 ${a.tone}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wider">{a.label}</div>
            <div className="mt-0.5 text-lg font-semibold">{buckets[a.key] ?? 0}</div>
          </div>
        ))}
      </section>

      {/* Rows table */}
      <section>
        <table className="w-full text-xs border-collapse" data-testid="freight-outstanding-table">
          <thead className="text-left text-[10px] uppercase tracking-wider text-slate-600 border-b border-slate-300">
            <tr>
              <th className="py-1.5 pr-2">Supplier</th>
              <th className="py-1.5 pr-2">Fuel Invoice</th>
              <th className="py-1.5 pr-2">Delivery Date</th>
              <th className="py-1.5 pr-2">Product(s)</th>
              <th className="py-1.5 pr-2 text-right">Litres</th>
              <th className="py-1.5 pr-2 text-right">Supplier Cost</th>
              <th className="py-1.5 pr-2 text-right">Days Awaiting</th>
              <th className="py-1.5 pr-2">Freight Status</th>
            </tr>
          </thead>
          <tbody>
            {(data.outstanding || []).length === 0 ? (
              <tr>
                <td colSpan={8} className="py-6 text-center text-slate-500 italic"
                    data-testid="freight-outstanding-empty">
                  All freight invoices are matched — nothing awaiting.
                </td>
              </tr>
            ) : (
              data.outstanding.map((row) => {
                const b = AGING.find((x) => x.key === row.aging_bucket);
                const products = (row.products || []).map((p) => p.product_label || p.product_code).filter(Boolean).join(" · ") || "—";
                return (
                  <tr key={row.receipt_id} className="border-b border-slate-100"
                      data-testid={`freight-outstanding-row-${row.receipt_id}`}>
                    <td className="py-1.5 pr-2">{row.supplier || "—"}</td>
                    <td className="py-1.5 pr-2 font-mono">{row.invoice_number || "—"}</td>
                    <td className="py-1.5 pr-2 font-mono">{row.delivery_date || "—"}</td>
                    <td className="py-1.5 pr-2">{products}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{fmtL(row.total_litres)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{fmtMoney(row.amount_due)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">
                      <span className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold ${b?.tone || "border-slate-200 bg-slate-50 text-slate-700"}`}
                            data-testid={`freight-outstanding-row-${row.receipt_id}-age`}>
                        {row.days_awaiting == null ? "—" : `${row.days_awaiting} d`}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2 text-[11px] text-amber-900">{row.freight_status || "Awaiting freight"}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function SummaryCard({ label, value, testid }) {
  return (
    <div className="rounded border border-slate-200 bg-white px-3 py-2" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-slate-900">{value}</div>
    </div>
  );
}
